import PhotoCameraRounded from '@mui/icons-material/PhotoCameraRounded';
import { Box, IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from '@mui/material';
import AddPhotoAlternateRounded from '@mui/icons-material/AddPhotoAlternateRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useRef, useState, type ReactNode } from 'react';
import { api, unwrap } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { imageProblem, IMAGE_TYPES } from '../features/catalog/model';
import { useNotify } from '../lib/notify';
import { useImageSrc } from '../lib/images';
import { ConfirmDialog } from './ConfirmDialog';

/** Which record an image belongs to: customer/supplier, category, staff member, yourself, the shop. */
export type ImageTarget =
  { kind: 'contact' | 'category' | 'staff'; id: string } | { kind: 'me' } | { kind: 'logo' };

function rawBody(file: File) {
  return {
    body: file as unknown as string,
    bodySerializer: (body: unknown) => body as BodyInit,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  };
}

function uploadImage(target: ImageTarget, file: File) {
  const raw = rawBody(file);
  switch (target.kind) {
    case 'contact':
      return unwrap(
        api.PUT('/contacts/{id}/image', { params: { path: { id: target.id } }, ...raw }),
      );
    case 'category':
      return unwrap(
        api.PUT('/categories/{id}/image', { params: { path: { id: target.id } }, ...raw }),
      );
    case 'staff':
      return unwrap(api.PUT('/staff/{id}/image', { params: { path: { id: target.id } }, ...raw }));
    case 'me':
      return unwrap(api.PUT('/me/image', raw));
    case 'logo':
      return unwrap(api.PUT('/settings/logo', raw));
  }
}

function removeImage(target: ImageTarget) {
  switch (target.kind) {
    case 'contact':
      return unwrap(api.DELETE('/contacts/{id}/image', { params: { path: { id: target.id } } }));
    case 'category':
      return unwrap(api.DELETE('/categories/{id}/image', { params: { path: { id: target.id } } }));
    case 'staff':
      return unwrap(api.DELETE('/staff/{id}/image', { params: { path: { id: target.id } } }));
    case 'me':
      return unwrap(api.DELETE('/me/image'));
    case 'logo':
      return unwrap(api.DELETE('/settings/logo'));
  }
}

/** Refetches every record that shows this image. */
async function refresh(qc: QueryClient, target: ImageTarget, reloadMe: () => Promise<void>) {
  const prefixes: Record<ImageTarget['kind'], string[][]> = {
    contact: [['contacts']],
    category: [['catalog', 'categories'], ['categories']],
    staff: [['staff']],
    me: [['staff']],
    logo: [['settings'], ['pos']],
  };
  await Promise.all(prefixes[target.kind].map((queryKey) => qc.invalidateQueries({ queryKey })));
  if (target.kind === 'me' || target.kind === 'staff') await reloadMe();
}

const NOUN: Record<ImageTarget['kind'], string> = {
  contact: 'photo',
  category: 'image',
  staff: 'photo',
  me: 'photo',
  logo: 'logo',
};

/**
 * Wraps an avatar with a camera button that uploads, replaces or removes the record's image.
 * Without `canEdit` it renders the avatar as is.
 */
export function ImageEditor({
  target,
  url,
  canEdit,
  label,
  children,
}: {
  target: ImageTarget;
  url: string | null | undefined;
  canEdit: boolean;
  /** Who or what the image is of, for the button's accessible name (e.g. "Sana Interiors"). */
  label: string;
  children: ReactNode;
}) {
  const qc = useQueryClient();
  const notify = useNotify();
  const { reloadMe } = useAuth();
  const input = useRef<HTMLInputElement>(null);
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [confirm, setConfirm] = useState(false);
  const noun = NOUN[target.kind];

  const upload = useMutation({
    mutationFn: (file: File) => uploadImage(target, file),
    onSuccess: async () => {
      await refresh(qc, target, reloadMe);
      notify.success(
        url ? `New ${noun} saved` : `${noun[0]?.toUpperCase() ?? ''}${noun.slice(1)} added`,
      );
    },
    onError: notify.error,
  });
  const remove = useMutation({
    mutationFn: () => removeImage(target),
    onSuccess: async () => {
      await refresh(qc, target, reloadMe);
      notify.success(`${noun[0]?.toUpperCase() ?? ''}${noun.slice(1)} removed`);
    },
    onError: notify.error,
  });

  if (!canEdit) return <>{children}</>;

  const pick = () => {
    setMenu(null);
    input.current?.click();
  };
  const onFile = (file: File | undefined) => {
    if (!file) return;
    const problem = imageProblem(file);
    if (problem) notify.error(new Error(problem));
    else upload.mutate(file);
  };

  return (
    <>
      <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
        {children}
        <Tooltip title={url ? `Change ${noun}` : `Add ${noun}`} describeChild>
          <IconButton
            size="small"
            aria-label={`${url ? 'Change' : 'Add'} ${noun} for ${label}`}
            aria-haspopup={url ? 'menu' : undefined}
            loading={upload.isPending || remove.isPending}
            onClick={(e) => (url ? setMenu(e.currentTarget) : pick())}
            sx={(t) => ({
              position: 'absolute',
              right: -6,
              bottom: -6,
              width: 28,
              height: 28,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              boxShadow: (t.vars ?? t).shadows[2],
              '&:hover': { bgcolor: 'action.hover' },
              '& svg': { fontSize: 16 },
            })}
          >
            <PhotoCameraRounded />
          </IconButton>
        </Tooltip>
      </Box>
      <input
        ref={input}
        type="file"
        hidden
        accept={IMAGE_TYPES.join(',')}
        aria-label={`Choose ${noun} file`}
        onChange={(e) => {
          onFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem onClick={pick}>
          <ListItemIcon>
            <AddPhotoAlternateRounded fontSize="small" />
          </ListItemIcon>
          Upload a new {noun}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenu(null);
            setConfirm(true);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}>
            <DeleteOutlineRounded fontSize="small" />
          </ListItemIcon>
          Remove {noun}
        </MenuItem>
      </Menu>
      <ConfirmDialog
        open={confirm}
        title={`Remove ${noun}?`}
        body={`The ${noun} of ${label} will be removed. You can upload another one any time.`}
        confirmLabel="Remove"
        danger
        onConfirm={() => remove.mutateAsync()}
        onClose={() => setConfirm(false)}
      />
    </>
  );
}

/** The record's image filling its box (`fallback` shows while there is none or it is loading). */
export function ImageFill({
  url,
  alt = '',
  fit = 'cover',
  fallback,
}: {
  url: string | null | undefined;
  alt?: string;
  /** `contain` keeps the whole picture visible (logos); `cover` fills the box. */
  fit?: 'cover' | 'contain';
  fallback: ReactNode;
}) {
  const { src } = useImageSrc(url);
  if (!src) return <>{fallback}</>;
  return (
    <img
      src={src}
      alt={alt}
      style={{
        width: '100%',
        height: '100%',
        objectFit: fit,
        display: 'block',
        boxSizing: 'border-box',
        padding: fit === 'contain' ? '8%' : 0,
      }}
    />
  );
}
